/**
 * usePWA - Progressive Web App install/update management
 * Extracted from App.js Phase 5 refactoring
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { registerServiceWorker, applyUpdate, isInstalledPWA, getInstallPlatform } from '../utils/pwaUpdate';

const usePWA = () => {
  const [pwaUpdateAvailable, setPwaUpdateAvailable] = useState(false);
  const [pwaRegistration, setPwaRegistration] = useState(null);
  const [showPwaInstallModal, setShowPwaInstallModal] = useState(false);
  const [pwaInstallPlatform, setPwaInstallPlatform] = useState(null);
  const [isPwaInstalled, setIsPwaInstalled] = useState(() => isInstalledPWA());
  const [canShowInstallPrompt, setCanShowInstallPrompt] = useState(false);

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      window.deferredPWAPrompt = e;
      console.log('[PWA] Install prompt captured - app NOT installed');
      setIsPwaInstalled(false);
      setCanShowInstallPrompt(true);
    });

    window.addEventListener('appinstalled', () => {
      console.log('[PWA] App installed successfully');
      window.deferredPWAPrompt = undefined;
      setIsPwaInstalled(true);
      setCanShowInstallPrompt(false);
    });
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          console.log('[PWA] Service worker updated to version:', event.data.version);
          toast.success('App updated! Refreshing...', { duration: 2000 });
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      });
    }
    
    registerServiceWorker((registration) => {
      console.log('[PWA] Update available, showing notification');
      setPwaRegistration(registration);
      setPwaUpdateAvailable(true);
    });
    
    const installed = isInstalledPWA();
    setIsPwaInstalled(installed);
    if (installed) {
      console.log('[PWA] Running as installed app');
    }
    
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e) => {
      console.log('[PWA] Display mode changed:', e.matches ? 'standalone' : 'browser');
      setIsPwaInstalled(e.matches);
    };
    mediaQuery.addEventListener('change', handleDisplayModeChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleDisplayModeChange);
    };
  }, []);

  const handlePwaUpdate = useCallback(() => {
    if (pwaRegistration) {
      applyUpdate(pwaRegistration);
    }
  }, [pwaRegistration]);

  const handlePwaInstallClick = useCallback(async () => {
    if (canShowInstallPrompt && window.deferredPWAPrompt) {
      try {
        window.deferredPWAPrompt.prompt();
        const { outcome } = await window.deferredPWAPrompt.userChoice;
        console.log('[PWA] Install prompt outcome:', outcome);
        if (outcome === 'accepted') {
          toast.success('SmartPlaite installed successfully!');
          setIsPwaInstalled(true);
        }
        window.deferredPWAPrompt = undefined;
        setCanShowInstallPrompt(false);
      } catch (err) {
        console.error('[PWA] Install prompt error:', err);
      }
      return;
    }
    
    const platform = getInstallPlatform();
    setPwaInstallPlatform(platform);
    setShowPwaInstallModal(true);
  }, [canShowInstallPrompt]);

  return {
    pwaUpdateAvailable,
    setPwaUpdateAvailable,
    showPwaInstallModal,
    setShowPwaInstallModal,
    pwaInstallPlatform,
    isPwaInstalled,
    canShowInstallPrompt,
    handlePwaUpdate,
    handlePwaInstallClick
  };
};

export default usePWA;
